/**
 * Formats a number as currency with pound sign (£) and thousand separators
 */
export function formatCurrency(value: number): string {
  return `£${value.toLocaleString('en-GB', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/**
 * Formats a number as a percentage with % sign
 */
export function formatPercentage(value: number): string {
  return `${value.toFixed(2)}%`;
}

export const UK_TIME_ZONE = 'Europe/London';

/**
 * Month tokens, fixed. NOT derived from Intl: `en-GB` with `month: 'short'` emits
 * "Sept" for September (every other month matches), where the .NET lane's
 * `UkTime` renders "Sep" via InvariantCulture. Deriving the token would put the
 * two lanes one byte apart for one month in twelve (preservoir-ai#82, D-2).
 */
const UK_MONTHS_SHORT = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

/**
 * The London wall-clock parts of an instant, as numbers.
 *
 * Uses `en-GB` + `formatToParts` with an explicit `timeZone`, so the values are
 * London's — never the browser's. Only numeric parts are read here; the month
 * NAME comes from UK_MONTHS_SHORT above.
 */
function londonParts(date: Date): {
  day: string;
  month: number;
  year: string;
  hour: string;
  minute: string;
} {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: UK_TIME_ZONE,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(date);

  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? '';

  return {
    day: get('day'),
    month: Number(get('month')),
    year: get('year'),
    // en-GB renders midnight as "24" under hour12: false; London's 00:xx must
    // read "00:xx" to match the .NET lane's HH:mm.
    hour: get('hour') === '24' ? '00' : get('hour'),
    minute: get('minute'),
  };
}

/**
 * The UK zone label in force at the instant — the literal "BST" or "GMT".
 *
 * Mirrors `UkTime.ZoneAbbreviation`: "GMT" is the correct winter label even
 * though the offset is +00:00 — never "UTC".
 */
export function londonZoneLabel(date: Date): string {
  const label = new Intl.DateTimeFormat('en-GB', {
    timeZone: UK_TIME_ZONE,
    timeZoneName: 'short',
  })
    .formatToParts(date)
    .find((part) => part.type === 'timeZoneName')?.value;

  return label === 'BST' ? 'BST' : 'GMT';
}

/**
 * Formats the LONDON calendar date of an instant (DD/MM/YYYY).
 *
 * The zone is pinned, not just the locale: an instant at 23:30 UTC on 15 June is
 * 16 June in London, and the day itself is therefore zone-dependent.
 *
 * NOTE: this converts an INSTANT. A date the user picked themselves (a browser-local
 * midnight out of the Calendar component) must NOT come through here — see
 * `formatPickedDate`.
 */
export function formatDate(date: Date): string {
  const { day, month, year } = londonParts(date);
  return `${day}/${String(month).padStart(2, '0')}/${year}`;
}

export function safeFormatDate(date: Date | undefined): string {
  if (!date) {
    return '';
  }
  return formatDate(date);
}

/**
 * Renders a date the USER picked, in the user's own zone (DD/MM/YYYY).
 *
 * Deliberately NOT London-pinned. The Calendar component hands back
 * `new Date(year, month, day)` — a browser-LOCAL midnight standing for the day the
 * user clicked, not an instant on the timeline. Converting it to London would make a
 * user west of London see their own "1 June" pick echoed back as "31 May"
 * (preservoir-ai#82, CL T6).
 */
export function formatPickedDate(date: Date): string {
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  return `${day}/${month}/${date.getFullYear()}`;
}

/**
 * Formats an instant as a client-facing UK wall-clock stamp — "08 Sep 2026, 15:30 BST".
 *
 * Byte-for-byte the `UkTime.Format` output of the .NET lane (AC5), so the same
 * instant reads identically in the app and in an email.
 */
export function formatLondonDateTime(date: Date): string {
  const { day, month, year, hour, minute } = londonParts(date);
  return `${day} ${UK_MONTHS_SHORT[month - 1]} ${year}, ${hour}:${minute} ${londonZoneLabel(date)}`;
}

/**
 * Formats the LONDON calendar day of an instant as DD/MM/YYYY.
 *
 * Same output as `formatDate`; a distinct name so a date-only display site reads as
 * a deliberate calendar-day choice rather than an instant that lost its time.
 */
export function formatCalendarDate(date: Date): string {
  return formatDate(date);
}

/**
 * The London calendar day of an instant as `YYYY-MM-DD` — a grouping/sort key, not
 * display copy. Two instants share a key exactly when they fall on the same London day.
 */
export function londonDayKey(date: Date): string {
  const { day, month, year } = londonParts(date);
  return `${year}-${String(month).padStart(2, '0')}-${day}`;
}

/**
 * The London calendar day/month/year of an instant, as numbers — for callers that
 * need to group or label by London day rather than render a string.
 */
export function londonDateParts(date: Date): { day: number; month: number; year: number } {
  const { day, month, year } = londonParts(date);
  return { day: Number(day), month: month - 1, year: Number(year) };
}

/**
 * Formats an instant's London time of day as HH:mm (24-hour).
 */
export function formatLondonTime(date: Date): string {
  const { hour, minute } = londonParts(date);
  return `${hour}:${minute}`;
}

/**
 * Formats an instant's London day and short month — "15 Jun".
 */
export function formatLondonDayMonth(date: Date): string {
  const { day, month } = londonParts(date);
  return `${Number(day)} ${UK_MONTHS_SHORT[month - 1]}`;
}

/**
 * Returns a friendly relative time string (e.g. "3 months ago", "in 2 days")
 */
export function formatRelativeTime(date: Date): string {
  const now = new Date();
  const diffInMs = date.getTime() - now.getTime();
  const diffInDays = Math.round(diffInMs / (1000 * 60 * 60 * 24));

  if (diffInDays === 0) return 'Today';
  if (diffInDays === 1) return 'Tomorrow';
  if (diffInDays === -1) return 'Yesterday';

  if (diffInDays > 0) {
    if (diffInDays < 30) return `in ${diffInDays} days`;
    if (diffInDays < 365) {
      const months = Math.round(diffInDays / 30);
      return `in ${months} ${months === 1 ? 'month' : 'months'}`;
    }
    const years = Math.round(diffInDays / 365);
    return `in ${years} ${years === 1 ? 'year' : 'years'}`;
  } else {
    const absDiff = Math.abs(diffInDays);
    if (absDiff < 30) return `${absDiff} days ago`;
    if (absDiff < 365) {
      const months = Math.round(absDiff / 30);
      return `${months} ${months === 1 ? 'month' : 'months'} ago`;
    }
    const years = Math.round(absDiff / 365);
    return `${years} ${years === 1 ? 'year' : 'years'} ago`;
  }
}

/**
 * Hour-aware phrasing of a future duration, consistent with the MFA email
 * copy: returns e.g. "1 hour", "24 hours", "45 minutes", "1 hour 30 minutes".
 *
 * Computed from the target instant (never a hardcoded number), so the phrasing
 * always reflects the real server window. A past/now target returns
 * "less than a minute".
 */
export function formatExpiryDuration(target: Date, from: Date = new Date()): string {
  const totalMinutes = Math.round((target.getTime() - from.getTime()) / 60000);
  if (totalMinutes <= 0) return 'less than a minute';

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  const parts: string[] = [];
  if (hours > 0) parts.push(`${hours} ${hours === 1 ? 'hour' : 'hours'}`);
  if (minutes > 0) parts.push(`${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`);
  return parts.join(' ');
}

/**
 * Returns the ordinal suffix for a day number (e.g. 1 -> 'st', 2 -> 'nd')
 */
export function getOrdinalSuffix(day: number): string {
  if (day > 3 && day < 21) return 'th';
  switch (day % 10) {
    case 1:
      return 'st';
    case 2:
      return 'nd';
    case 3:
      return 'rd';
    default:
      return 'th';
  }
}

/**
 * Returns the first two initials from a name (e.g. 'John Doe' -> 'JD')
 */
export function getInitials(name: string): string {
  return name
    .split(' ')
    .map((word) => word.charAt(0))
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

/**
 * Formats a large number with K/M/B suffixes (e.g. 1500 -> 1.5K)
 */
export function formatCompactNumber(value: number): string {
  if (value < 1000) return value.toString();

  const tiers = [
    { threshold: 1e12, suffix: 'T' },
    { threshold: 1e9, suffix: 'B' },
    { threshold: 1e6, suffix: 'M' },
    { threshold: 1e3, suffix: 'K' },
  ];

  for (const { threshold, suffix } of tiers) {
    if (value >= threshold) {
      return (value / threshold).toFixed(1).replace(/\.0$/, '') + suffix;
    }
  }

  return value.toString();
}

/**
 * Formats a UK sort code for display as `XX-XX-XX`.
 *
 * `BankAccount.SortCode` is an unvalidated string server-side, so real data arrives in
 * both shapes: bare digits (`"000001"` — the dev seed / StructuralBankIdentity default)
 * and already-hyphenated (`"04-13-76"` — the prod config form). Normalise to digits
 * first, then group, so both render identically.
 *
 * Anything that isn't exactly 6 digits is passed through unchanged rather than being
 * mangled into a plausible-looking but wrong sort code — displaying a bank destination
 * incorrectly is worse than displaying it unformatted.
 */
export function formatSortCode(sortCode: string): string {
  if (!sortCode) return '';

  const digits = sortCode.replace(/\D/g, '');
  if (digits.length !== 6) return sortCode;

  return `${digits.slice(0, 2)}-${digits.slice(2, 4)}-${digits.slice(4, 6)}`;
}
